import React, { useEffect, useState } from "react";
import { Navigate, Link } from "react-router-dom";
import SiteHeader from "../components/SiteHeader";
import api, { formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Users, Copy, Trash2, LogOut, Plus, Building2, ArrowRight, Mail, Upload, Palette } from "lucide-react";
import { Textarea } from "@/components/ui/textarea";

export default function Team() {
  const { user, ready, refresh } = useAuth();
  const [data, setData] = useState(null);
  const [name, setName] = useState("");
  const [inviteEmail, setInviteEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/workspace/me");
      setData(data);
    } catch {
      setData({ workspace: null });
    }
  };

  useEffect(() => {
    if (ready && user) load();
  }, [ready, user]);

  if (!ready) return null;
  if (!user) return <Navigate to="/login" replace />;

  const isOwner = data?.workspace?.role === "owner";

  const createWs = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/workspace/create", { name });
      toast.success("Workspace created");
      await refresh();
      await load();
      setName("");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  const invite = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/workspace/invite", { email: inviteEmail });
      toast.success("Invite sent. Copy the link if the email hasn't arrived.");
      setInviteEmail("");
      await load();
      // auto-copy link
      try { await navigator.clipboard.writeText(data.invite_link); } catch {}
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  const revokeInvite = async (id) => {
    try {
      await api.delete(`/workspace/invites/${id}`);
      await load();
    } catch { toast.error("Failed"); }
  };

  const removeMember = async (id) => {
    if (!window.confirm("Remove this member from the workspace?")) return;
    try {
      await api.delete(`/workspace/members/${id}`);
      await load();
    } catch { toast.error("Failed"); }
  };

  const leave = async () => {
    if (!window.confirm(isOwner ? "This will delete the workspace. Continue?" : "Leave workspace?")) return;
    try {
      await api.post("/workspace/leave");
      toast.success(isOwner ? "Workspace deleted" : "You've left the workspace");
      await refresh();
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <SiteHeader />
      <main className="max-w-5xl mx-auto px-6 lg:px-10 py-12">
        <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500">Team</div>
        <h1 className="font-heading text-4xl font-bold tracking-tight mt-2 flex items-center gap-3">
          <Users className="w-7 h-7 text-[#0F172A]" /> Workspace
        </h1>

        {!data ? (
          <div className="mt-10 text-zinc-500 font-mono text-sm">Loading…</div>
        ) : !data.workspace ? (
          <div className="mt-10 grid md:grid-cols-2 gap-4">
            <div className="border border-zinc-200 bg-white rounded-sm p-8" data-testid="team-create-card">
              <Building2 className="w-6 h-6 text-[#0F172A]" />
              <h2 className="font-heading text-2xl font-bold tracking-tight mt-3">Create a workspace</h2>
              <p className="text-sm text-zinc-600 mt-2">
                Share expert-review credits and CoS history with your firm.
                Any credits you already own will move into the shared pool.
              </p>
              <form onSubmit={createWs} className="mt-6 space-y-3">
                <Label className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500" htmlFor="wsname">Firm name</Label>
                <Input id="wsname" required value={name} onChange={(e) => setName(e.target.value)}
                       className="rounded-sm h-10" placeholder="e.g. Sleeba Immigration Ltd" data-testid="team-create-name" />
                <Button type="submit" disabled={loading || !name.trim()}
                        className="w-full rounded-sm bg-[#0F172A] hover:bg-[#0F172A]/90 h-11"
                        data-testid="team-create-submit">
                  <Plus className="w-4 h-4" /> Create workspace
                </Button>
              </form>
            </div>
            <div className="border border-zinc-200 bg-white rounded-sm p-8">
              <Mail className="w-6 h-6 text-[#0F172A]" />
              <h2 className="font-heading text-2xl font-bold tracking-tight mt-3">Joining a team?</h2>
              <p className="text-sm text-zinc-600 mt-2">
                If a colleague has invited you, use the link they sent. It looks like{" "}
                <span className="font-mono text-xs text-zinc-800">/join/&lt;token&gt;</span>. Sign in first with the email they invited.
              </p>
            </div>
          </div>
        ) : (
          <div className="mt-10 grid lg:grid-cols-12 gap-6">
            {/* Workspace summary */}
            <div className="lg:col-span-4 border border-[#0F172A] bg-[#0F172A] text-white rounded-sm p-6">
              <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/60">Workspace</div>
              <div className="font-heading text-2xl font-bold tracking-tight mt-1" data-testid="team-name">{data.workspace.name}</div>
              <div className="mt-6 border-t border-white/20 pt-5">
                <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/60">Shared credits</div>
                <div className="font-heading text-5xl font-bold tracking-tighter mt-1" data-testid="team-credits">
                  {data.workspace.review_credits}
                </div>
                <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/60 mt-1">
                  expert reviews available
                </div>
              </div>
              <Link to="/#pricing" className="mt-6 block">
                <Button className="w-full rounded-sm bg-white text-[#0F172A] hover:bg-zinc-100" data-testid="team-buy-more">
                  Buy the 3-Pack <ArrowRight className="w-4 h-4" />
                </Button>
              </Link>
              <button onClick={leave} className="mt-4 w-full text-white/60 hover:text-white text-xs font-mono uppercase tracking-[0.15em] inline-flex items-center gap-2 justify-center" data-testid="team-leave">
                <LogOut className="w-3 h-3" /> {isOwner ? "Delete workspace" : "Leave workspace"}
              </button>
            </div>

            {/* Members + invites */}
            <div className="lg:col-span-8 space-y-4">
              <div className="border border-zinc-200 bg-white rounded-sm">
                <div className="px-5 py-3 border-b border-zinc-200 flex items-center justify-between">
                  <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-600">
                    Members · {data.members.length}
                  </div>
                </div>
                <ul className="divide-y divide-zinc-100" data-testid="team-members-list">
                  {data.members.map((m) => (
                    <li key={m.id} className="px-5 py-3 flex items-center justify-between">
                      <div className="min-w-0">
                        <div className="font-heading font-semibold truncate">{m.name || m.email}</div>
                        <div className="font-mono text-xs text-zinc-500 truncate">{m.email}</div>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className={`font-mono text-[10px] uppercase tracking-[0.15em] px-2 py-0.5 rounded-sm border ${
                          m.role === "owner"
                            ? "bg-[#0F172A] text-white border-[#0F172A]"
                            : "bg-zinc-100 text-zinc-700 border-zinc-300"
                        }`}>{m.role}</span>
                        {isOwner && m.role !== "owner" && (
                          <button onClick={() => removeMember(m.id)}
                                  className="text-zinc-400 hover:text-red-700"
                                  data-testid={`team-remove-${m.id}`}>
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              </div>

              {isOwner && (
                <BrandingCard ws={data.workspace} onSaved={load} />
              )}
              {isOwner && (
                <div className="border border-zinc-200 bg-white rounded-sm p-5">
                  <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-600">Invite a colleague</div>
                  <form onSubmit={invite} className="mt-3 flex gap-2">
                    <Input type="email" required placeholder="colleague@firm.co.uk"
                           value={inviteEmail} onChange={(e) => setInviteEmail(e.target.value)}
                           className="rounded-sm h-10 flex-1" data-testid="team-invite-email" />
                    <Button type="submit" disabled={loading || !inviteEmail}
                            className="rounded-sm bg-[#0F172A] hover:bg-[#0F172A]/90 h-10"
                            data-testid="team-invite-btn">
                      Send invite
                    </Button>
                  </form>

                  {data.invites.length > 0 && (
                    <div className="mt-5">
                      <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-2">
                        Pending invites
                      </div>
                      <ul className="divide-y divide-zinc-100 border border-zinc-200 rounded-sm">
                        {data.invites.map((inv) => {
                          const link = `${window.location.origin}/join/${inv.token}`;
                          return (
                            <li key={inv.id} className="px-4 py-2 flex items-center justify-between text-sm gap-2">
                              <div className="min-w-0 flex-1">
                                <div className="font-mono text-xs truncate">{inv.email}</div>
                                <div className="font-mono text-[10px] text-zinc-500 truncate">{link}</div>
                              </div>
                              <button onClick={async () => {
                                try { await navigator.clipboard.writeText(link); toast.success("Link copied"); } catch {}
                              }} className="text-zinc-500 hover:text-zinc-900" data-testid={`team-copy-${inv.id}`}>
                                <Copy className="w-4 h-4" />
                              </button>
                              <button onClick={() => revokeInvite(inv.id)}
                                      className="text-zinc-400 hover:text-red-700" data-testid={`team-revoke-${inv.id}`}>
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

function BrandingCard({ ws, onSaved }) {
  const [footer, setFooter] = useState(ws.report_footer || "");
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const inputRef = React.useRef(null);
  const logoSrc = ws.has_logo && ws.logo_url ? ws.logo_url : null;

  const upload = async (file) => {
    if (!file) return;
    if (!["image/png", "image/jpeg", "image/webp"].includes(file.type)) {
      toast.error("Logo must be PNG, JPEG or WebP");
      return;
    }
    if (file.size > 500 * 1024) {
      toast.error("Logo must be under 500 KB");
      return;
    }
    setUploading(true);
    const form = new FormData();
    form.append("file", file);
    try {
      await api.post("/workspace/logo", form, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Logo uploaded");
      onSaved();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setUploading(false); }
  };

  const removeLogo = async () => {
    setUploading(true);
    try {
      await api.delete("/workspace/logo");
      toast.success("Logo removed");
      onSaved();
    } catch (err) {
      toast.error(err.message);
    } finally { setUploading(false); }
  };

  const saveFooter = async () => {
    setSaving(true);
    try {
      await api.patch("/workspace/branding", { report_footer: footer });
      toast.success("Branding saved");
      onSaved();
    } catch (err) {
      toast.error(err.message);
    } finally { setSaving(false); }
  };

  return (
    <div className="border border-zinc-200 bg-white rounded-sm p-5" data-testid="team-branding-card">
      <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-600 flex items-center gap-2">
        <Palette className="w-3.5 h-3.5" /> Report branding
      </div>
      <p className="text-sm text-zinc-600 mt-1">
        Your logo and footer text will appear on every downloaded PDF report your team generates.
      </p>
      <div className="mt-4 grid md:grid-cols-2 gap-4">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">Firm logo</div>
          <div className="mt-2 h-28 border border-dashed border-zinc-300 rounded-sm grid place-items-center bg-zinc-50" data-testid="team-logo-preview">
            {logoSrc ? (
              <img src={`${process.env.REACT_APP_BACKEND_URL}${logoSrc}`} alt="firm logo" className="max-h-24 max-w-[80%] object-contain" />
            ) : (
              <div className="text-zinc-400 text-xs font-mono uppercase tracking-[0.2em]">No logo</div>
            )}
          </div>
          <input ref={inputRef} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={(e) => e.target.files?.[0] && upload(e.target.files[0])} data-testid="team-logo-input" />
          <div className="mt-3 flex gap-2">
            <Button variant="outline" size="sm" onClick={() => inputRef.current?.click()} disabled={uploading} className="rounded-sm" data-testid="team-logo-upload">
              <Upload className="w-3.5 h-3.5" /> {ws.has_logo ? "Replace" : "Upload"}
            </Button>
            {ws.has_logo && (
              <Button variant="outline" size="sm" onClick={removeLogo} disabled={uploading} className="rounded-sm text-red-700 border-red-200 hover:bg-red-50" data-testid="team-logo-remove">
                <Trash2 className="w-3.5 h-3.5" /> Remove
              </Button>
            )}
          </div>
          <div className="mt-2 font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500">
            PNG · JPEG · WebP · max 500 KB
          </div>
        </div>
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">Report footer text</div>
          <Textarea rows={5} value={footer} onChange={(e) => setFooter(e.target.value)}
                    placeholder="e.g. Prepared by Sleeba Immigration Ltd. Not affiliated with UKVI."
                    className="rounded-sm mt-2" maxLength={200} data-testid="team-footer-textarea" />
          <div className="mt-2 flex justify-end">
            <Button size="sm" onClick={saveFooter} disabled={saving} className="rounded-sm bg-[#0F172A] hover:bg-[#0F172A]/90" data-testid="team-footer-save">
              Save footer
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
